import { Alert, Grid, Theme, Typography, useTheme } from '@mui/material';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import debounce from 'lodash/debounce';
import { useCallback } from 'react';
import { MessageUsersTemplate } from '../../hooks/useMessageUsersTextTemplates';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import { isSextforceActive, loadDraftsJSEditorStateFromHtml } from '../../utils/common';
import MessageUsersFormEditiorPreview from '../services/messageUsers/sendMessage/MessageUsersFormEditiorPreview';
import MessageUsersFormEditorTemplateSave from '../services/messageUsers/sendMessage/MessageUsersFormEditorTemplateSave';
import OnlyFansQuillMessageEditor from './OnlyFansQuillMessageEditor';

type Props = {
    subscriber: OnlyFansSubscriber;
    message: string;
    setMessage: (message: string) => void;
    messageHasRestrictedWords: boolean;
    messageFoundRestrictedWords: string[];
    checkForRestrictedWords: (text: string) => void;
    taggedCreators: { id: number; name: string }[];
    taggedReleaseForms: { id: number; name: string }[];
    lockMessage: boolean;
    lockMessagePrice: number;
    lockedText: boolean;
    saveTemplate: boolean;
    setSaveTemplate: (saveTemplate: boolean) => void;
    saveTemplateName: string;
    setSaveTemplateName: (templateName: string) => void;
    setUsernameReplacement: (usernameReplacement: string) => void;
    showUsernamePlaceholder: boolean;
    showSaveTemplate?: boolean;
    showPreview?: boolean;
    disabled?: boolean;
};

const OnlyFansMessageEditorWithPreview = (props: Props) => {
    const {
        subscriber,
        message,
        setMessage,
        messageHasRestrictedWords,
        messageFoundRestrictedWords,
        checkForRestrictedWords,
        taggedCreators,
        taggedReleaseForms,
        lockMessage,
        lockMessagePrice,
        lockedText,
        saveTemplate,
        setSaveTemplate,
        saveTemplateName,
        setSaveTemplateName,
        showUsernamePlaceholder,
        setUsernameReplacement,
        showSaveTemplate = true,
        showPreview = true,
        disabled,
    } = props;
    const theme: Theme = useTheme();

    const debouncedSetMessage = useCallback(
        debounce((newState: EditorState) => {
            setMessage(customConvertToHTML(newState.getCurrentContent()));

            const rawText = newState.getCurrentContent().getPlainText();

            checkForRestrictedWords(rawText);
        }, 300), // 300ms debounce delay
        [setMessage, checkForRestrictedWords],
    );

    const customConvertToHTML = (contentState: ContentState) => {
        const rawContentState = convertToRaw(contentState);
        let html = draftToHtml(rawContentState, undefined, undefined, customEntityTransform);

        // Convert consecutive paragraphs to line breaks within a single paragraph
        // This handles the main case: <p>text</p><p>text</p> -> <p>text<br />text</p>
        html = html.replace(/<\/p>\s*<p>/g, '<br />');

        // Handle empty paragraphs by converting them to line breaks
        // Remove standalone empty paragraphs: <p></p> -> <br />
        html = html.replace(/<p><\/p>/g, '<br />');

        // Clean up multiple consecutive <br /> tags (optional - keeps double line breaks)
        // html = html.replace(/(<br \/>){3,}/g, '<br /><br />');

        // Remove trailing <br /> tags at the very end
        html = html.replace(/(<br \/>)+\s*$/, '');

        // Ensure content is wrapped in a paragraph if it exists
        if (html && !html.startsWith('<p>')) {
            html = `<p>${html}</p>`;
        }

        // Handle edge case where we might have empty content
        if (html === '<p></p>') {
            html = '';
        }

        // <p><span class="m-editor-fs__lg">Largest</span><br><span class="m-editor-fs__l">Large</span><br><span class="m-editor-fs__default">Default</span><br><span class="m-editor-fs__s">Small</span><br><span class="m-editor-fs__sm">Smallest</span><br><span class="m-editor-fs__default m-editor-fc__blue-2">Dark Blue</span><br><span class="m-editor-fs__default m-editor-fc__blue-1">Light Blue</span><br><span class="m-editor-fs__default m-editor-fc__gray">Grey</span><br><span class="m-editor-fs__default m-editor-fc__gray"><strong>Bold</strong></span><br><span class="m-editor-fs__default m-editor-fc__gray"><em><strong>Italic</strong></em></span><br></p>

        // Replace color spans
        html = html.replace(/<span style="color: #00aff0;"/g, '<span class="m-editor-fc__blue-1"');
        html = html.replace(/<span style="color: #1b98e0;"/g, '<span class="m-editor-fc__blue-2"');
        html = html.replace(/<span style="color: #8a96a3;"/g, '<span class="m-editor-fc__gray"');
        html = html.replace(/<span style="color: rgb\(0,0,0\);"/g, '<span class="m-editor-fc__default"');

        // Replace font size spans
        html = html.replace(/<span style="font-size: 24px;"/g, '<span class="m-editor-fs__lg"');
        html = html.replace(/<span style="font-size: 20px;"/g, '<span class="m-editor-fs__l"');
        html = html.replace(/<span style="font-size: 16px;"/g, '<span class="m-editor-fs__default"');
        html = html.replace(/<span style="font-size: 14px;"/g, '<span class="m-editor-fs__s"');
        html = html.replace(/<span style="font-size: 12px;"/g, '<span class="m-editor-fs__sm"');

        return html;
    };

    const customEntityTransform = (entity: any, text: string) => {
        if (entity.type === 'LINK') {
            return `<a href="${entity.data.url}">${text}</a>`;
        }
        return text;
    };

    const handleLoadTemplate = (template: MessageUsersTemplate) => {
        const newState = loadDraftsJSEditorStateFromHtml(template.message);

        debouncedSetMessage(newState);

        if (template.usernameReplacement) {
            setUsernameReplacement(template.usernameReplacement);
        }
    };

    return (
        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
            <Grid item xs={12}>
                <OnlyFansQuillMessageEditor
                    valueOF={message}
                    onChangeOF={newText => setMessage(newText)}
                    placeholder=""
                    showUsernameButton={showUsernamePlaceholder}
                    showSaveTemplate={true}
                />
            </Grid>
            {messageHasRestrictedWords && (
                <Grid item xs={12}>
                    <Alert
                        variant="filled"
                        severity={isSextforceActive(subscriber) && subscriber.sextforce?.bigBrother?.active ? 'error' : 'warning'}
                    >
                        Message contains words that are restricted by OnlyFans!
                        {messageFoundRestrictedWords.length > 0 && (
                            <>
                                <br />
                                {messageFoundRestrictedWords.join(', ')}
                            </>
                        )}
                    </Alert>
                </Grid>
            )}
            {showSaveTemplate && (
                <Grid item xs={12}>
                    <MessageUsersFormEditorTemplateSave
                        subscriber={subscriber}
                        saveTemplate={saveTemplate}
                        setSaveTemplate={setSaveTemplate}
                        saveTemplateName={saveTemplateName}
                        setSaveTemplateName={setSaveTemplateName}
                        handleLoadTemplate={handleLoadTemplate}
                        disabled={disabled}
                    />
                </Grid>
            )}
            {showPreview && (
                <>
                    <Grid item xs={12}>
                        <Typography variant="inherit" sx={{ marginTop: 1 }}>
                            Preview
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <MessageUsersFormEditiorPreview
                            message={message}
                            taggedCreators={taggedCreators}
                            taggedReleaseForms={taggedReleaseForms}
                            lockMessage={lockMessage}
                            lockMessagePrice={lockMessagePrice}
                            lockedText={lockedText}
                            theme={theme}
                            disabled={disabled}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default OnlyFansMessageEditorWithPreview;
